import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCircleCheck, faDiagramProject, faHand, faToolbox } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

// ─────────────────────────────────────────────────────────────────────────────
// RuleBuilderOnboarding
//
// First-run tour for the rule builder. Instead of a centered modal that talks
// ABOUT the UI, this spotlights the real features in place: a dimmed lightbox
// overlay with a clear cut-out + teal ring around the actual element, and a
// coachmark card anchored next to it. Users get oriented around the live UI.
//
// Each step targets an element via a `data-onboarding="…"` attribute (added on
// the toolbox/elements pane, the canvas, and the Save button). If a target is
// missing (e.g. the footer isn't rendered for a brand-new rule), that step
// gracefully falls back to a centered card with a full dim.
//
// Persists a localStorage flag so it shows once; Next / Back / Skip / Got it.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "coapp-rule-builder-onboarding-seen";

const GRADIENT = "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)";

const CARD_WIDTH = 360;
const CARD_HEIGHT_EST = 230; // used only for flip/clamp math

type Step = {
	icon: IconDefinition;
	title: string;
	body: string;
	/** CSS selector for the element to spotlight. Omit for a centered step. */
	target?: string;
};

const STEPS: Step[] = [
	{
		icon: faHand,
		title: "Welcome to the Rule Builder",
		body: "This is where you turn an idea into an automation — visually, no code. Here's a 20-second tour of the actual features you'll use.",
	},
	{
		icon: faToolbox,
		title: "Your building blocks live here",
		body: "Search for and grab steps and variables from this panel. When you open a step, its settings take over this same column.",
		target: "[data-onboarding='elements']",
	},
	{
		icon: faDiagramProject,
		title: "This is your flow",
		body: "Your rule runs top-to-bottom along the arrows. Click the + on any node to add the next step — a Reaction, Filter, Decision, Loop, and more. A Decision splits the flow into True / False branches.",
		target: "[data-onboarding='canvas']",
	},
	{
		icon: faCircleCheck,
		title: "Save when you're ready",
		body: "Give your rule a name and hit Save. You can always come back and edit it later. That's it — happy building!",
		target: "[data-onboarding='save']",
	},
];

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

type RuleBuilderOnboardingProps = {
	/**
	 * Gate the first-run tour until the surrounding flow is ready — for a brand
	 * new rule this is set false while the "Create Your Rule" modal is open, so
	 * the tour appears only AFTER the user completes that pop-up the first time.
	 * For an existing rule (no modal) it's true on mount, so the tour shows right
	 * away. Defaults to true.
	 */
	ready?: boolean;
};

export default function RuleBuilderOnboarding({ ready = true }: RuleBuilderOnboardingProps) {
	const [open, setOpen] = useState(false);
	const [step, setStep] = useState(0);
	const [rect, setRect] = useState<DOMRect | null>(null);

	useEffect(() => {
		if (!ready) return;
		try {
			if (!window.localStorage?.getItem(STORAGE_KEY)) {
				setOpen(true);
			}
		} catch {
			// localStorage unavailable (private mode) — just don't show the tour.
		}
	}, [ready]);

	const current = STEPS[step];

	// Measure the current step's target element. Recompute on step change, on
	// resize/scroll, and a couple of delayed passes to catch layout settling
	// (e.g. the canvas fitView animation or the panel mounting).
	const measure = useCallback(() => {
		const target = STEPS[step]?.target;
		if (!target) {
			setRect(null);
			return;
		}
		const el = document.querySelector(target);
		setRect(el ? el.getBoundingClientRect() : null);
	}, [step]);

	useEffect(() => {
		if (!open) return;
		measure();
		const t1 = window.setTimeout(measure, 80);
		const t2 = window.setTimeout(measure, 400);
		window.addEventListener("resize", measure);
		window.addEventListener("scroll", measure, true);
		return () => {
			window.clearTimeout(t1);
			window.clearTimeout(t2);
			window.removeEventListener("resize", measure);
			window.removeEventListener("scroll", measure, true);
		};
	}, [open, measure]);

	const finish = () => {
		try {
			window.localStorage?.setItem(STORAGE_KEY, "1");
		} catch {
			// ignore storage errors
		}
		setOpen(false);
	};

	if (!open) return null;

	const isLast = step === STEPS.length - 1;
	const hasSpot = Boolean(rect);

	// ── Card position ──────────────────────────────────────────────────────
	const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
	const vh = typeof window !== "undefined" ? window.innerHeight : 800;
	const gap = 16;
	let cardLeft: number;
	let cardTop: number;
	let cardTransform = "none";

	if (!rect) {
		cardLeft = vw / 2;
		cardTop = vh / 2;
		cardTransform = "translate(-50%, -50%)";
	} else {
		const tall = rect.height > vh * 0.5;
		if (tall && rect.right + gap + CARD_WIDTH < vw) {
			// Anchor to the right of a tall target (e.g. the side panel).
			cardLeft = rect.right + gap;
			cardTop = clamp(rect.top, 16, vh - CARD_HEIGHT_EST - 16);
		} else if (tall && rect.left - gap - CARD_WIDTH > 0) {
			// Tall target hugging the right edge → anchor to its left.
			cardLeft = rect.left - gap - CARD_WIDTH;
			cardTop = clamp(rect.top, 16, vh - CARD_HEIGHT_EST - 16);
		} else {
			// Place below; flip above if it would overflow the viewport.
			cardLeft = clamp(rect.left, 16, vw - CARD_WIDTH - 16);
			cardTop = rect.bottom + gap;
			if (cardTop + CARD_HEIGHT_EST > vh - 16) {
				cardTop = clamp(rect.top - gap - CARD_HEIGHT_EST, 16, vh - CARD_HEIGHT_EST - 16);
			}
		}
	}

	const spotPad = 6;

	return createPortal(
		<Box sx={{ position: "fixed", inset: 0, zIndex: 13000 }}>
			{/* Click-catcher: blocks interaction with the page behind. When no
			    target is found it also supplies the full dim; when a target is
			    found the dim comes from the spotlight ring's box-shadow. */}
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					background: hasSpot ? "transparent" : "rgba(13, 31, 48, 0.62)",
					transition: "background 0.25s ease",
					pointerEvents: "auto",
				}}
			/>

			{/* Spotlight ring — dims everything outside the cut-out via a huge
			    box-shadow, and rings the live element in brand teal. */}
			{rect && (
				<Box
					sx={{
						position: "fixed",
						top: rect.top - spotPad,
						left: rect.left - spotPad,
						width: rect.width + spotPad * 2,
						height: rect.height + spotPad * 2,
						borderRadius: "12px",
						boxShadow: "0 0 0 9999px rgba(13, 31, 48, 0.62)",
						border: "2px solid #02D5AB",
						pointerEvents: "none",
						transition: "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				/>
			)}

			{/* Coachmark card */}
			<Box
				sx={{
					position: "fixed",
					top: cardTop,
					left: cardLeft,
					transform: cardTransform,
					width: CARD_WIDTH,
					maxWidth: "92vw",
					borderRadius: 4,
					overflow: "hidden",
					background: "#FFFFFF",
					boxShadow: "0 18px 48px rgba(13, 31, 48, 0.28), 0 0 0 1px rgba(13, 31, 48, 0.04)",
					pointerEvents: "auto",
					transition: "top 0.28s cubic-bezier(0.4, 0, 0.2, 1), left 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
				}}
			>
				<div style={{ background: GRADIENT, padding: "20px 22px 16px", color: "#FFFFFF", display: "flex", alignItems: "center", gap: 12 }}>
					<FontAwesomeIcon icon={current.icon} style={{ color: "#FFFFFF", fontSize: 20, flexShrink: 0 }} />
					<Typography sx={{ fontSize: 18, fontWeight: 800, color: "#FFFFFF" }}>
						{current.title}
					</Typography>
				</div>

				<div style={{ padding: "16px 22px 4px" }}>
					<Typography sx={{ fontSize: 14, color: "rgba(13, 31, 48, 0.75)", lineHeight: 1.6 }}>
						{current.body}
					</Typography>
					<div style={{ display: "flex", gap: 6, marginTop: 18 }}>
						{STEPS.map((_, i) => (
							<div
								key={i}
								style={{
									width: i === step ? 22 : 8,
									height: 8,
									borderRadius: 999,
									transition: "all 0.2s ease",
									background: i === step ? GRADIENT : "rgba(13, 31, 48, 0.12)",
								}}
							/>
						))}
					</div>
				</div>

				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px 18px" }}>
					<Button onClick={finish} sx={{ textTransform: "none", fontWeight: 600, color: "rgba(13, 31, 48, 0.55)" }}>
						Skip
					</Button>
					<div style={{ display: "flex", gap: 8 }}>
						{step > 0 ? (
							<Button
								onClick={() => setStep((s) => s - 1)}
								sx={{ textTransform: "none", fontWeight: 600, color: "#057A6D", whiteSpace: "nowrap" }}
							>
								Back
							</Button>
						) : null}
						<Button
							onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
							variant="contained"
							sx={{
								textTransform: "none",
								fontWeight: 700,
								borderRadius: 999,
								px: 2.5,
								whiteSpace: "nowrap",
								background: GRADIENT,
								boxShadow: "0 4px 12px rgba(2, 213, 171, 0.30)",
								":hover": { background: GRADIENT, filter: "brightness(1.06)" },
							}}
						>
							{isLast ? "Got it" : "Next"}
						</Button>
					</div>
				</div>
			</Box>
		</Box>,
		document.body,
	);
}
